import React, { useState, useEffect, useRef } from 'react';
import notificationSound from '../assets/sounds/notification.mp3';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive,
  Class as ClassIcon,
  Assignment,
  Message as MessageIcon,
  Payment,
  Schedule,
  Report,
  QuestionAnswer,
  CheckCircle,
  Delete,
  MarkEmailRead
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import socketService from '../services/socket';
import { notificationAPI } from '../services/api';

const NotificationBell = ({ userType }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const listenerSetup = useRef(false);

  const open = Boolean(anchorEl);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Fetch all notifications (limit set to 200 to get all recent notifications)
      const response = await notificationAPI.getAll({ limit: 200 });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => (n._id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await notificationAPI.delete(notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    markAsRead(notification._id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    handleClose();
  };

  // Socket listener for new notifications
  useEffect(() => {
    const handleNewNotification = (notification) => {
     

      setNotifications(prev => {
        const updated = [notification, ...prev];
        return updated;
      });

      setUnreadCount(prev => {
        const newCount = prev + 1;
        return newCount;
      });

      // Play notification sound
      if (audioRef.current) {
        audioRef.current.play().catch(err => console.log('🔔 NotificationBell: Audio play error:', err));
      } else {
        console.log('🔔 NotificationBell: Audio ref not available');
      }

    };

    const setupSocketListener = () => {
      const socket = socketService.getSocket();

      if (!socket) {
        return false;
      }

      if (listenerSetup.current) {
        return true;
      }


      socket.on('new_notification', handleNewNotification);
      listenerSetup.current = true;

      return true;
    };

    // Try to setup listener immediately
    if (!setupSocketListener()) {
      // If failed, retry every 500ms for up to 5 seconds
      let attempts = 0;
      const maxAttempts = 10;

      const retryInterval = setInterval(() => {
        attempts++;

        if (setupSocketListener() || attempts >= maxAttempts) {
          clearInterval(retryInterval);
          if (attempts >= maxAttempts && !listenerSetup.current) {
            console.error('❌ NotificationBell: Failed to set up socket listener after all attempts');
          }
        }
      }, 500);

      return () => {
        clearInterval(retryInterval);
        const socket = socketService.getSocket();
        if (socket) {
          socket.off('new_notification', handleNewNotification);
          listenerSetup.current = false;
        }
      };
    }

    return () => {
      const socket = socketService.getSocket();
      if (socket) {
        socket.off('new_notification', handleNewNotification);
        listenerSetup.current = false;
      }
    };
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
      fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    const iconMap = {
      class_scheduled: <ClassIcon />,
      class_cancelled: <ClassIcon />,
      class_rescheduled: <Schedule />,
      quicktest_assigned: <Assignment />,
      quicktest_graded: <CheckCircle />,
      homework_assigned: <Assignment />,
      homework_graded: <CheckCircle />,
      invoice_generated: <Payment />,
      invoice_due: <Payment />,
      payment_received: <Payment />,
      message_received: <MessageIcon />,
      trial_request: <QuestionAnswer />,
      reschedule_request: <Schedule />,
      reschedule_approved: <CheckCircle />,
      reschedule_rejected: <Schedule />,
      report_available: <Report />
    };
    return iconMap[type] || <NotificationsIcon />;
  };

  // Get color based on notification type
  const getNotificationColor = (type) => {
    if (type.includes('class')) return 'primary';
    if (type.includes('quicktest') || type.includes('homework')) return 'secondary';
    if (type.includes('invoice') || type.includes('payment')) return 'warning';
    if (type.includes('message')) return 'info';
    if (type.includes('request')) return 'error';
    return 'default';
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    const colorMap = {
      high: 'error',
      medium: 'warning',
      low: 'info'
    };
    return colorMap[priority] || 'default';
  };

  // Format time ago
  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <>
      {/* Hidden audio for notification sound */}
      <audio ref={audioRef} src={notificationSound} preload="auto" />

      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{ ml: 1 }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? (
              <NotificationsActive sx={{ color: 'primary.main' }} />
            ) : (
              <NotificationsIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 600,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead} startIcon={<MarkEmailRead />}>
                Mark all read
              </Button>
            )}
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{
          flex: 1,
              maxHeight: 400, 
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            }
          }
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification._id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      '&:hover': {
                        bgcolor: 'action.selected'
                      },
                      py: 2
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${getNotificationColor(notification.type)}.main` }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: notification.isRead ? 400 : 600 }}>
                            {notification.title}
                          </Typography>
                          {notification.priority === 'high' && (
                            <Chip label="High" size="small" color="error" sx={{ height: 18 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {timeAgo(notification.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
